// Never import any server-only code here, this is also used on the client side...
import type { TranslationsMap } from './types';

// English version of the translation file
export const translations: TranslationsMap = {
  emails: {
    footer: {
      kindRegards: 'Z poważaniem,',
      questionsOrProblems: 'Pytania lub problemy?',
      contactUs: 'Skontaktuj się z nami',
    },
    transportOrder: { confirm: 'Potwierdź', refuse: 'Odrzuć' },
    forgotPassword: { resetLink: 'Skonfiguruj nowe hasło' },
    userInvite: { view: 'Zobacz zaproszenie' },
    attachments: { quotation: 'Wycena {{reference}}' },
    customerComplaintAlert: { order: 'Zobacz zamówienie' },
  },
  truckDocumentType: { Insurance: 'Ubezpieczenie', Examination: 'Badanie techniczne' },
  orderDocumentType: {
    cmr: 'CMR',
    deliveryNote: 'Delivery note',
    weighSlip: 'Weigh slip',
    palletNote: 'Pallet note',
    annexII: 'Annex II',
    t1Document: 'T1 document',
    annexVII: 'Annex VII',
    saleInvoice: 'Invoice',
    transportOrder: 'Transport order',
    orderConfirmation: 'Order confirmation',
    sustainabilityReport: 'Sustainability report',
    customerAttachment: 'Customer attachment',
    planning: 'Planning',
    other: 'Other',
  },
  documentRejection: {
    subject: 'Dokument odrzucony dla zlecenia {{orderNumber}}',
    intro: 'Nie mogliśmy zatwierdzić dokumentu {{documentType}} otrzymanego dla zlecenia {{orderNumber}}. Powody:',
    outro: 'Prosimy o ponowne przesłanie poprawionego dokumentu zwykłym kanałem lub przez portal przewoźnika.',
    MissingSignature: 'Brak podpisu odbiorcy na CMR.',
    WrongCustomer: 'Klient na dokumencie nie zgadza się z klientem tego zlecenia.',
    WrongOrgOnDocument: 'Nadawca/spedytor na dokumencie nie zgadza się z naszą organizacją.',
    NoOrderReference: 'Nie znaleziono prawidłowego odwołania do zlecenia ani faktury.',
    WrongDocumentType: 'Typ dokumentu nie odpowiada oczekiwanemu typowi dla tego zlecenia.',
    LowQuality: 'Jakość skanu/zdjęcia jest zbyt niska, aby wiarygodnie zweryfikować treść.',
  },
  trailerTypes: {
    BoxTrailer: 'Naczepa typu chłodnia/izoterma',
    FlatBed: 'Platforma',
    Mega: 'Naczepa Mega',
    Refrigerated: 'Chłodnia',
    RoadTrain: 'Pociąg drogowy',
    Tautliner: 'Firanka',
    Joloda: 'Firanka / Joloda',
    TautlinerCoil: 'Firanka / Coilmulda',
  },
  loadingMethods: {
    Back: 'Tył',
    Side: 'Bok',
    Top: 'Przez dach',
  },
  documents: {
    note: { extraInformation: 'Dodatkowe informacje' },
    letter: { letter: 'List' },
    paymentReminder: {
      invoiceNumber: 'Numer faktury',
      invoiceDate: 'Data faktury',
      dueDate: 'Termin płatności',
      amount: 'Kwota',
      expiredAmount: 'Kwota przeterminowana',
      currency: 'Waluta',
      totalOutstanding: 'Total outstanding',
      salutation: 'Dear Sir or Madam,',
      subject: {
        first: 'Subject: payment reminder — {{reference}}',
        second: 'Subject: second payment reminder — {{reference}}',
        third: 'Subject: third payment reminder — {{reference}}',
        final: 'Subject: notice of default — {{reference}}',
      },
      intro: {
        first:
          'Our records show that the invoices below have not yet been settled. If your payment has crossed with this letter, please disregard it.',
        second: 'Despite our earlier reminder, we have not yet received payment for the invoices below:',
        third: 'Despite repeated reminders, the invoices below are still outstanding:',
        final: 'Despite earlier payment reminders, we have to date received no payment for the invoices below:',
      },
      request:
        'We request that you transfer the outstanding amount of {{amount}} to the account below within {{days}} calendar days of receiving this letter, quoting the reference:',
      acknowledgement:
        'If you do not pay and do not state a reason for this, it will be regarded as an acknowledgement of the debt.',
      escalation: {
        first: 'Should we not receive your payment within this period, we will send you a further reminder.',
        second: 'Should we not receive your payment within this period, we will be obliged to escalate this file.',
        third:
          'Should payment not be made within this period, this file will be transferred to our debt collection procedure.',
        final:
          'Should payment not be made within this period, we will hand this file to our legal counsel without further notice, with instructions to start legal proceedings against you. The costs of this will be entirely at your expense.',
      },
      closing: {
        first: 'We trust you will settle the outstanding amount within the requested period.',
        second: 'We trust you will settle the outstanding amount within the requested period.',
        third: 'We trust you will settle the outstanding amount within the requested period.',
        final:
          'We trust this will not be necessary and that you will settle the outstanding amount within the requested period.',
      },
      sincerely: 'Sincerely,',
      signature: 'Accounting {{company}}',
      account: 'Account {{company}}',
      accounts: 'Rachunki {{company}}',
      reference: 'Reference',
      amountDue: 'Amount due',
      bic: 'BIC',
      heldBy: 'właściciel {{company}}',
      date: 'Date',
      ourReference: 'Our reference',
      customerNumber: 'Customer number',
      yourVatNumber: 'Your VAT number',
      contact: 'Contact',
      phone: 'Phone',
    },
    quotation: {
      quotation: 'Wycena',
      customerNumber: 'Numer klienta',
      reference: 'Numer referencyjny',
      date: 'Data wyceny',
      dueDate: 'Data ważności',
      from: 'Z',
      to: 'Do',
      volume: 'Objętość',
      price: 'Cena',
      generalTermsAndConditions: 'Ogólne warunki sprzedaży w załączeniu',
      questionsOrComments: 'W przypadku pytań prosimy o kontakt mailowy na adres {{email}}',
      trailerTypes: 'Typy naczep',
    },
    transportOrder: {
      trailerTypes: 'Typy naczep',
      loadingMethod: 'Sposób załadunku',
      transportOrder: 'Zlecenie transportowe',
      date: 'Data',
      orderRef: 'Nr referencyjny zlecenia',
      load: 'Załadunek',
      unload: 'Rozładunek',
      reference: 'Numer referencyjny',
      goods: 'Towar',
      description: 'Opis',
      packageType: 'Rodzaj palety',
      palletCount: 'Liczba palet',
      weight: 'Waga',
      LoadingMeter: 'Metry ładunkowe',
      freightRate: 'Stawka frachtowa',
      price: 'Cena',
      vat: 'VAT',
      questionsOrComments: 'Pytania lub uwagi?',
      contactFileManager: 'Skontaktuj się ze swoim menedżerem plików',
      anonymousCmr: 'DLA TEGO ZLECENIA NALEŻY UTWORZYĆ ANONIMOWY CMR.',
      phone: 'Telefon',
      email: 'E-mail',
    },
    planning: {
      planning: 'Planowanie',
      licensePlate: 'Numer rejestracyjny',
      codeName: 'Nazwa kodowa',
    },
    kilometers: {
      full: 'Kilometry z ładunkiem',
      empty: 'Kilometry bez ładunku',
      total: 'Kilometry razem',
      planningTotal: 'Suma dla tego planowania',
    },
    orderConfirmation: {
      orderConfirmation: 'Potwierdzenie zamówienia',
      dearCustomer: 'Szanowny Kliencie,',
      herebyWeConfirmYourOrder: 'Niniejszym potwierdzamy Twoje zamówienie na następujący transport:',
      date: 'Data',
      ourReference: 'Nasz numer referencyjny',
      yourReference: 'Państwa numer referencyjny',
      load: 'Załadunek',
      unload: 'Rozładunek',
      reference: 'Numer referencyjny',
      goods: 'Towar',
      description: 'Opis',
      packageType: 'Rodzaj palety',
      palletCount: 'Liczba palet',
      weight: 'Waga',
      LoadingMeter: 'Metry ładunkowe',
      freightRate: 'Stawka frachtowa',
      price: 'Cena',
      vat: 'VAT',
      questionsOrComments: 'Pytania lub uwagi?',
      contactFileManager: 'Skontaktuj się ze swoim menedżerem plików',
      phone: 'Telefon',
      email: 'E-mail',
    },
    orderCancellation: {
      orderCancellation: 'Anulowanie zamówienia',
      dearCustomer: 'Szanowny Kliencie,',
      orderCancelledText: 'Twoje zamówienie na poniższy transport zostało anulowane:',
      date: 'Data',
      ourReference: 'Nasz numer referencyjny',
      yourReference: 'Państwa numer referencyjny',
      load: 'Załadunek',
      unload: 'Rozładunek',
      reference: 'Numer referencyjny',
      goods: 'Towar',
      description: 'Opis',
      packageType: 'Rodzaj palety',
      palletCount: 'Liczba palet',
      weight: 'Waga',
      LoadingMeter: 'Metry ładunkowe',
      freightRate: 'Stawka frachtowa',
      price: 'Cena',
      vat: 'VAT',
      questionsOrComments: 'Pytania lub uwagi?',
      contactFileManager: 'Skontaktuj się ze swoim menedżerem plików',
      phone: 'Telefon',
      email: 'E-mail',
    },
    invoice: {
      invoice: 'Faktura',
      customerNumber: 'Numer klienta',
      invoiceNumber: 'Numer faktury',
      invoiceDate: 'Data faktury',
      dueDate: 'Termin płatności',
      load: 'Załadunek',
      unload: 'Rozładunek',
      ourReference: 'Nasz numer referencyjny',
      yourReference: 'Państwa numer referencyjny',
      description: 'Opis',
      quantity: 'Ilość',
      unitPrice: 'Cena jednostkowa',
      total: 'Razem',
      vat: 'VAT',
      paymentInformation: 'Informacje o płatności',
      amountToPay: 'Kwota do zapłaty',
      toBePaidBefore: 'Płatne do',
      beneficiary: 'Odbiorca',
      message: 'Wiadomość',
      generalTermsAndConditions: 'Ogólne warunki sprzedaży w załączeniu',
      questionsOrComments: 'W przypadku pytań prosimy o kontakt z działem księgowości pod adresem {{email}}',
    },
    sustainabilityReport: {
      sustainabilityReport: 'Raport Zrównoważonego Rozwoju',
      date: 'Data',
      ourReference: 'Nasz numer referencyjny',
      yourReference: 'Państwa numer referencyjny',
      truck: 'Ciężarówka',
      distance: 'Dystans',
      weight: 'Waga',
      emissions: 'Emisje',
      questionsOrComments: 'Pytania lub uwagi?',
      contactFileManager: 'Skontaktuj się ze swoim menedżerem plików',
      phone: 'Telefon',
      email: 'E-mail',
    },
    globalSustainabilityReport: {
      sustainabilityReport: 'Raport Zrównoważonego Rozwoju',
      date: 'Data',
      startDate: 'Data początkowa',
      endDate: 'Data końcowa',
      orderCount: 'Liczba zamówień',
      truck: 'Ciężarówka',
      totals: 'Suma',
      distance: 'Dystans',
      weight: 'Waga',
      emissions: 'Emisje CO2',
      averagePerOrder: 'Średnio na zamówienie',
      framework:
        'Szczegółowe informacje na temat obliczania emisji znajdują się w polityce zrównoważonego rozwoju na naszej stronie internetowej.',
      questionsOrComments: 'Pytania lub uwagi?',
      contactFileManager: 'Skontaktuj się ze swoim menedżerem plików',
      phone: 'Tel.',
      email: 'Email',
    },
    rideOverview: {
      title: 'Zestawienie',
      entryNumber: 'Numer zestawienia',
      dossier: 'Akta',
      draftBanner: 'Wersja robocza — jeszcze nie zatwierdzona',
      invoiceRequestNotice:
        'Ten dokument nie jest ważną fakturą samofakturującą. Po otrzymaniu tego zestawienia {{customer}} oczekuje od Państwa faktury za te zlecenia.',
    },
    selfBill: {
      title: 'Samofakturowanie',
      number: 'Nr',
      draftBanner: 'Wersja robocza — jeszcze nie zatwierdzona',
      supplierLabel: 'Dostawca — przewoźnik · płacimy tej firmie',
      customerLabel: 'Nabywca — wystawia tę fakturę w imieniu dostawcy',
      vatCompanyNumber: 'NIP / nr rejestrowy:',
      vatShort: 'NIP',
      carrierNumber: 'Numer przewoźnika: {{number}}',
      customerRegister: 'RLE Gent, division Kortrijk',
      bankLabel: 'Płatność na rachunek bankowy dostawcy',
      bankNotice:
        'Zarejestrowany w naszej kartotece przewoźników. Jeśli to nie jest Państwa rachunek, prosimy o natychmiastowy kontakt z {{email}}. Dane bankowe zmieniamy wyłącznie w ramach naszej procedury weryfikacji, nigdy na podstawie wiadomości e-mail.',
      documentType: 'Rodzaj dokumentu',
      invoiceNumber: 'Numer faktury',
      invoiceDate: 'Data faktury',
      servicePeriod: 'Okres świadczenia usług',
      agreement: 'Umowa',
      paymentDate: 'Data płatności',
      natureOfServices:
        'Rodzaj usług: {{nature}}, jeden przejazd na wiersz, wykonany w podanej dacie wysyłki. Cena jednostkowa = kwota wiersza (ilość 1), bez VAT.',
      roadFreightTransport: 'drogowy transport towarów',
      columnNumber: 'Nr',
      columnFile: 'Zlecenie',
      columnShipmentDate: 'Data wysyłki',
      columnLoading: 'Załadunek',
      columnUnloading: 'Rozładunek',
      columnQuantity: 'Il.',
      columnVat: 'VAT',
      columnAmount: 'EUR',
      tripCountOne: 'Liczba przejazdów na tej fakturze: 1',
      tripCountOther: 'Liczba przejazdów na tej fakturze: {{count}}',
      totalServices: 'Razem usługi transportowe (bez VAT)',
      earlyPaymentDiscount: 'Rabat za szybką płatność {{percentage}}',
      earlyPaymentCondition:
        'Przy płatności w ciągu 7 dni odlicza się rabat w wysokości {{percentage}} ({{amount}}). Do zapłaty: {{withDiscount}}. Po upływie tego terminu: {{withoutDiscount}}.',
      taxableAmount: 'Podstawa opodatkowania {{rate}}',
      vat: 'VAT {{rate}}',
      totalToPay: 'Do zapłaty',
      paymentNotice:
        '{{amount}} zostanie automatycznie przelane w dniu {{date}} na rachunek {{iban}}. Nie są wymagane żadne działania z Państwa strony.',
      paymentNoticeWithoutAccount:
        '{{amount}} zostanie automatycznie przelane w dniu {{date}}. Nie są wymagane żadne działania z Państwa strony.',
      yourInvoiceTitle: 'To jest Państwa faktura. Prosimy nie wysyłać nam faktury za te przejazdy.',
      yourInvoice:
        'Ten dokument wystawia {{customer}} w imieniu i na rzecz {{supplier}} na podstawie umowy o samofakturowanie zawartej między stronami. Nie jest to propozycja. Faktura wystawiona przez Państwa za te same przejazdy zostanie odrzucona jako duplikat.',
      acceptanceTitle: 'Akceptacja',
      acceptance:
        'Faktura zostaje uznana za zaakceptowaną przez dostawcę, o ile nie zostanie zakwestionowana na piśmie pod adresem {{email}} w ciągu 7 dni od daty faktury. Korekty dokonywane są fakturą korygującą w ramach samofakturowania.',
      paymentTermTitle: 'Termin płatności',
      paymentTerm:
        'Obowiązuje termin płatności uzgodniony w Państwa umowie. Jeśli udzielono rabatu, termin płatności wynosi 7 dni.',
      missingTripTitle: 'Brakuje przejazdu?',
      missingTrip:
        'Przejazd nie znalazł się na tej fakturze, ponieważ jego dokumenty nie są w porządku. Po przesłaniu właściwych dokumentów w portalu przewoźnika przejazd zostanie ujęty na kolejnej fakturze.',
      questionsTitle: 'Pytania?',
      questions: 'Prosimy o kontakt pod adresem {{email}} z podaniem numeru faktury.',
      legalSelfBilling: 'Samofakturowanie',
      legalNotice:
        '{{selfBilling}} — faktura wystawiona przez nabywcę w imieniu i na rzecz dostawcy (art. 224 i 226 pkt 10a dyrektywy 2006/112/WE) · Umowa o samofakturowanie {{agreement}} · Peppol BIS Self-Billing 3.0, typ dokumentu 389',
      legalTerms:
        'Ogólne warunki sprzedaży w załączniku i na stronie {{website}} · Obowiązuje prawo belgijskie · Właściwe są sądy w Kortrijk',
      pageFooter: '{{title}} {{number}} · {{company}}',
      page: 'Strona {{page}} z {{total}}',
    },
    creditNote: {
      creditNote: 'Nota kredytowa',
      customerNumber: 'Numer klienta',
      creditNoteNumber: 'Numer noty kredytowej',
      date: 'Data',
      dueDate: 'Termin płatności',
      load: 'Załadunek',
      unload: 'Rozładunek',
      ourReference: 'Nasz numer referencyjny',
      yourReference: 'Państwa numer referencyjny',
      description: 'Opis',
      quantity: 'Ilość',
      unitPrice: 'Cena jednostkowa',
      total: 'Razem',
      vat: 'VAT',
      paymentInformation: 'Informacje o płatności',
      amountToPay: 'Kwota do zapłaty',
      toBePaidBefore: 'Płatne do',
      payer: 'Płatnik',
      message: 'Wiadomość',
      generalTermsAndConditions: 'Ogólne warunki sprzedaży w załączeniu',
      questionsOrComments: 'W przypadku pytań prosimy o kontakt z działem księgowości pod adresem {{email}}',
    },
    carrierTruckReport: {
      title: 'Truck performance report',
      truckname: 'Name',
      totalIncome: 'Total income',
      totalKm: 'Total kilometers',
    },
    totals: {
      totalExclVat: 'Suma bez VAT',
      totalVat: 'Suma VAT',
      totalInclVat: 'Suma z VAT',
      cashDiscount: 'Rabat za wczesną płatność',
      netAmountToPay: 'Kwota netto do zapłaty',
      vatReverseCharged: 'Odwrotne obciążenie VAT, art. 44 dyrektywy VAT',
    },
  },
  carrierTerms: {
    agreement: 'Akceptuję {{terms}}, w tym procedurę samofakturowania.',
    termsLink: 'ogólne warunki',
  },
  pages: {
    confirmOrderStates: {
      headingRefused: 'Zamówienie zostało odrzucone',
      headingCancelled: 'Zamówienie zostało anulowane',
      headingConfirmed: 'Zamówienie zostało potwierdzone',
      headingUnknownStatus: 'Zamówienie zostało już zrealizowane lub status jest nieznany',
    },
    confirmOrderPlanningStates: {
      headingRefused: 'Planowanie zostało odrzucone',
      headingCancelled: 'Planowanie zostało anulowane',
      headingConfirmed: 'Planowanie zostało potwierdzone',
      headingUnknownStatus: 'Planowanie zostało już zrealizowane lub status jest nieznany',
    },
    confirmOrder: {
      heading: 'Potwierdź zlecenie transportowe {{order.orderNumber}}',
      orderConfirmed: 'Zamówienie potwierdzone',
      orderConfirmationError: 'Nie można potwierdzić zamówienia: {{error}}',
      confirmButton: 'Potwierdź zlecenie transportowe',
    },
    confirmPlanning: {
      heading: 'Potwierdź planowanie {{planningDate}}',
      planningConfirmed: 'Planowanie potwierdzone',
      confirmButton: 'Potwierdź planowanie',
    },
    refuseOrder: {
      heading: 'Odrzuć zlecenie transportowe {{order.orderNumber}}',
      orderRefused: 'Zamówienie odrzucone',
      orderRefusalError: 'Nie można odrzucić zamówienia: {{error}}',
      refuseButton: 'Odrzuć zlecenie transportowe',
    },
    refusePlanning: {
      heading: 'Odrzuć planowanie {{planningDate}}',
      planningRefused: 'Planowanie odrzucone',
      refuseButton: 'Odrzuć planowanie',
    },
    orderSummary: {
      loadAndUnloadInformation: 'Informacje o załadunku i rozładunku',
      load: 'Załadunek',
      unload: 'Rozładunek',
      freightRate: 'Stawka frachtowa',
      total: 'Razem',
    },
  },
  portal: {
    requestAccount: {
      requestCustomerAccount: 'Złóż wniosek o konto klienta',
      requestCarrierAccount: 'Złóż wniosek o konto przewoźnika',
      success: 'Wniosek został wysłany, skontaktujemy się z Tobą w celu omówienia dalszych kroków.',
      failed: 'Nie można złożyć wniosku o konto: {{error}}',
      name: 'Imię i nazwisko',
      email: 'Email',
      companyName: 'Nazwa firmy',
      companyNumber: 'Numer firmy',
      request: 'Złóż wniosek',
      dontHaveAnAccount: 'Nie masz jeszcze konta?',
      accountRequestFailed: 'Żądanie konta nie powiodło się',
    },
  },
  customerPortal: {
    switch: 'Przełącz klienta',
    selectCustomer: 'Wybierz klienta',
    requestAccount: {
      title: 'Złóż wniosek o konto',
      success: 'Wniosek został złożony, skontaktujemy się z Tobą w celu omówienia dalszych kroków.',
      failed: 'Nie można złożyć wniosku o konto: {{error}}',
      name: 'Imię i nazwisko',
      email: 'Email',
      companyName: 'Nazwa firmy',
      companyNumber: 'Numer firmy',
      request: 'Złóż wniosek',
      dontHaveAnAccount: 'Nie masz jeszcze konta?',
    },
    invite: {
      title: 'Zaproszenie do Portalu Klienta',
      details: 'Zostałeś zaproszony do portalu klienta TransDirect, utwórz swoje konto, wprowadzając hasło.',
      accepted: 'Zaproszenie przyjęte, wkrótce zostaniesz przekierowany na stronę główną.',
      acceptError: 'Nie można przyjąć zaproszenia: {{error}}',
      acceptButtonText: 'Przyjmij zaproszenie',
      password: 'Hasło',
      repeatPassword: 'Powtórz hasło',
    },
    home: {
      customerPortal: 'Portal klienta',
      title: 'Portal klienta',
      hello: 'Witaj,',
      welcome: 'Witaj w portalu klienta.',
      manageOrders: 'Zarządzaj wszystkimi swoimi zleceniami transportowymi.',
      viewInvoices: 'Łatwo przeglądaj i opłacaj faktury.',
      createQuotes: 'Twórz i śledź wyceny.',
      viewLocations: 'Przeglądaj wszystkie lokalizacje transportowe.',
    },
    locations: {
      locations: 'Lokalizacje',
      location: 'Lokalizacja',
      name: 'Nazwa',
      street: 'Ulica',
      number: 'Numer',
      countryCode: 'Kod kraju',
      postalCode: 'Kod pocztowy',
      city: 'Miasto',
      country: 'Kraj',
      language: 'Język',
      notes: 'Notatki',
      createLocation: 'Utwórz lokalizację',
      newLocation: 'Nowa lokalizacja',
      locationCreated: 'Lokalizacja utworzona',
      couldNotCreateLocation: 'Nie można utworzyć lokalizacji: {{error}}',
      findLocation: 'Znajdź lokalizację...',
      address: 'Adres',
      locationNotFound: 'Lokalizacja nie znaleziona',
      couldNotLoadLocation: 'Nie można załadować lokalizacji: {{error}}',
    },
    order: {
      duplicate: 'Duplikuj',
      bulkDuplicate: 'Duplikowanie masowe',
      bulkDuplicateTitle: 'Masowe duplikowanie zamówienia',
      bulkDuplicateDescription: 'Utwórz wiele kopii tego zamówienia z rosnącymi numerami referencyjnymi faktur.',
      numberOfDuplicates: 'Liczba duplikatów',
      baseInvoiceRef: 'Początkowy numer referencyjny faktury',
      baseInvoiceRefHelper: 'Baza numeryczna. Wiodące zera zostaną usunięte. Referencje będą baza do baza+liczba-1.',
      skipDraft: 'Pomiń status wersji roboczej (bezpośrednio jako Utworzone)',
      bulkDuplicateSuccess: '{{count}} zamówień utworzonych',
      bulkDuplicateError: 'Nie udało się zduplikować zamówień: {{error}}',
      referencePreview: 'Referencje: {{first}} do {{last}}',
      overview: 'Przegląd',
      updateInvoiceRef: 'Atnaujinti sąskaitos nuorodą',
      updated: 'Zamówienie zostało zaktualizowane',
      couldNotUpdate: 'Nie udało się zaktualizować zamówienia: {{error}}',
    },
    orders: {
      orders: 'Zamówienia',
      placeOrder: 'Złóż zamówienie',
      invoiceRef: 'Numer referencyjny faktury',
      orderRef: 'Numer referencyjny zamówienia',
      loadLocation: 'Miejsce załadunku',
      unloadLocation: 'Miejsce rozładunku',
      loadDate: 'Data załadunku',
      loadTime: 'Godzina załadunku',
      unloadDate: 'Data rozładunku',
      unloadTime: 'Godzina rozładunku',
      invoiceTotal: 'Suma faktury',
      status: 'Status',
      invoiceStatus: 'Status faktury',
      hasCMR: 'Posiada CMR',
      creationDate: 'Data utworzenia',
      executionDate: 'Data wykonania',
      fileManager: 'Menedżer plików',
      cancellationReason: 'Przyczyna anulowania',
      stops: 'Przystanki',
      deleteStop: 'Usuń przystanek',
      purchases: 'Zakupy',
      total: 'Razem',
      trailerTypes: 'Typy naczep',
      loadingMethod: 'Sposób załadunku',
      load: 'Załadunek',
      unload: 'Rozładunek',
      typeStop: 'Typ przystanku',
      extraNotes: 'Dodatkowe notatki',
      noContent: 'Brak zawartości',
      packaging: 'Opakowanie',
      coli: 'Coli',
      content: 'Zawartość',
      addContent: 'Dodaj zawartość',
      deleteContent: 'Usuń zawartość',
      noStops: 'Brak przystanków',
      noStopsError: 'Dodaj co najmniej jeden przystanek załadunku i rozładunku',
      reference: 'Numer referencyjny',
      complaints: 'Reklamacje',
      fileComplaint: 'Złóż reklamację',
      fileComplaintDescription: 'Wypełnij ten formularz, aby złożyć reklamację',
      send: 'Wyślij',
      reason: 'Powód',
      details: 'Szczegóły',
      complaintFiled: 'Reklamacja złożona',
      couldNotFileComplaint: 'Nie można złożyć reklamacji: {{error}}',
      placeAnOrder: 'Złóż zamówienie',
      assignment: 'Zadanie',
      addStop: 'Dodaj przystanek',
      location: 'Lokalizacja',
      date: 'Data',
      start: 'Start',
      stop: 'Stop',
      weight: 'Waga (kg)',
      volume: 'Objętość (m3)',
      loadMeter: 'Metry ładunkowe',
      stopsRequired: 'Wymagany jest co najmniej 1 przystanek',
      contentRequired: 'Wymagany jest co najmniej 1 element zawartości',
      truck: 'Ciężarówka',
      distance: 'Dystans',
      emissions: 'Emisje',
      sustainability: 'Zrównoważony rozwój',
      attachments: 'Załączniki',
      uploadAttachments: 'Prześlij załączniki',
      attachmentDeleted: 'Załącznik usunięty',
      couldNotDeleteAttachment: 'Nie można usunąć załącznika: {{error}}',
      uploadedAttachment: 'Załącznik {{document}} przesłany',
      uploadFailed: 'Nie można przesłać załącznika {{document}}: {{error}}',
      route: 'Trasa',
      chooseMethod: 'Wybierz metodę',
      automatic: 'Automatycznie',
      automaticDescription:
        'Prześlij jeden lub więcej plików PDF z zamówieniami transportowymi, a nasza AI automatycznie wyodrębni szczegóły zamówienia. Każdy PDF staje się osobnym zamówieniem.',
      manual: 'Ręcznie',
      manualDescription:
        'Wypełnij formularz zamówienia samodzielnie, podając wszystkie przystanki, ładunek i szczegóły dostawy.',
      uploadPdfs: 'Prześlij pliki PDF zamówień transportowych',
      processingPdf: 'Przetwarzanie PDF...',
      createOrders: 'Utwórz zamówienia',
      orderCreated: 'Zamówienie utworzone',
      ordersCreated: '{{count}} zamówień utworzonych',
      couldNotCreateFromPdf: 'Nie udało się utworzyć zamówienia z PDF: {{error}}',
      uploadAtLeastOnePdf: 'Prześlij co najmniej jeden PDF',
      viewOrder: 'Zobacz zamówienie',
      creatingOrders: 'Tworzenie zamówień...',
      report: 'Raport',
      orderPlaced: 'Zamówienie złożone',
      couldNotPlaceOrder: 'Nie można złożyć zamówienia: {{error}}',
      stopNumber: 'Przystanek {{number}} - {{type}}',
      orderLink: 'Zamówienie {{orderNumber}} - {{customerRef}}',
    },
    quotes: {
      quotes: 'Wyceny',
      quote: 'Wycena',
      quoteReference: 'Numer referencyjny wyceny',
      quoteDate: 'Data wyceny',
      expirationDate: 'Data ważności',
      total: 'Razem',
      search: 'Wyszukaj wycenę...',
      quotation: 'Wycena {{reference}}',
      notFound: 'Wycena nie znaleziona',
      departurePlace: 'Miejsce wyjazdu',
      destination: 'Miejsce docelowe',
      distance: 'Dystans',
      price: 'Cena',
      type: 'Typ',
      requests: 'Złożone zapytania o wycenę',
      request: 'Poproś o wycenę',
      requestTitle: 'Zapytanie o wycenę {{reference}}',
      notes: 'Notatki',
      lines: 'Linie wyceny',
      addLine: 'Dodaj linię',
      noLines: 'Brak linii wyceny',
      requestSuccess: 'Wycena zamówiona',
      requestFailed: 'Nie można zamówić wyceny: {{error}}',
      requestedAt: 'Zamówiono o',
      quotationRequests: 'Zapytania o wycenę',
      hasQuote: 'Posiada wycenę',
      generalInformation: 'Informacje ogólne',
      removeLine: 'Usuń linię',
    },
    quoteLine: { from: 'Z', to: 'Do' },
    invoices: {
      invoices: 'Faktury',
      invoiceNumber: 'Numer faktury',
      invoiceDate: 'Data faktury',
      dueDate: 'Termin płatności',
      total: 'Razem',
      search: 'Wyszukaj fakturę...',
      notFound: 'Faktura nie znaleziona',
      invoice: 'Faktura {{invoiceNumber}}',
      structuredComment: 'Komentarz strukturyzowany',
      reference: 'Numer referencyjny',
      sentAt: 'Wysłano o',
      load: 'Załadunek',
      unload: 'Rozładunek',
      description: 'Opis',
      amount: 'Kwota',
      unitPrice: 'Cena jednostkowa',
      vat: 'VAT',
      extraInformation: 'Dodatkowe informacje: {{extraInformation}}',
      orderTotal: 'Suma zamówienia: €{{total}}',
      totalExclVat: 'Suma bez VAT:',
      totalVat: 'Suma VAT:',
      totalInclVat: 'Suma z VAT:',
      generalInformation: 'Informacje ogólne',
    },
    creditNotes: {
      creditNotes: 'Noty kredytowe',
      creditNoteNumber: 'Numer noty kredytowej',
      creditNoteDate: 'Data noty kredytowej',
      dueDate: 'Termin płatności',
      total: 'Razem',
      search: 'Wyszukaj notę kredytową...',
      notFound: 'Nota kredytowa nie znaleziona',
      creditNote: 'Nota kredytowa {{creditNoteNumber}}',
      structuredComment: 'Komentarz strukturyzowany',
      reference: 'Numer referencyjny',
      sentAt: 'Wysłano dnia',
      load: 'Załadunek',
      unload: 'Rozładunek',
      description: 'Opis',
      amount: 'Kwota',
      unitPrice: 'Cena jednostkowa',
      vat: 'VAT',
      extraInformation: 'Dodatkowe informacje: {{extraInformation}}',
      orderTotal: 'Suma zamówienia: €{{total}}',
      totalExclVat: 'Suma bez VAT:',
      totalVat: 'Suma VAT:',
      totalInclVat: 'Suma z VAT:',
      generalInformation: 'Informacje ogólne',
    },
    businessInformation: {
      businessInformation: 'Informacje o firmie',
      updateBusinessInformation: 'Aktualizuj informacje o firmie',
      name: 'Nazwa',
      vatNumber: 'Numer VAT',
      businessNumber: 'Numer firmy',
      saveChanges: 'Zapisz zmiany',
      informationChanged: 'Informacje zostały zaktualizowane',
      informationNotChanged: 'Nie można zaktualizować informacji: {{error}}',
      requestChanges: 'Aby zaktualizować nazwę firmy, numer VAT lub numer firmy, skontaktuj się z nami',
    },
    ourInformation: {
      ourInformation: 'Informacje o firmie TransDirect',
      name: 'Nazwa',
      vatNumber: 'Numer VAT',
      businessNumber: 'Numer firmy',
      address: 'Adres',
      contact: 'Kontakt',
      phoneNumber: 'Numer telefonu',
      emailTransport: 'Email transportowy',
      emailAccounting: 'Email księgowy',
      emailGeneral: 'Email ogólny',
    },
    contacts: {
      contacts: 'Kontakty',
      name: 'Imię i nazwisko',
      email: 'Email',
      phone: 'Telefon',
      function: 'Funkcja',
      language: 'Język',
      notifications: 'Kontakt odpowiedni dla',
      newContact: 'Nowy kontakt',
      contactCreated: 'Kontakt utworzony',
      couldNotCreateContact: 'Nie można utworzyć kontaktu: {{error}}',
      createContact: 'Utwórz kontakt',
      notFound: 'Kontakt nie znaleziony',
      updatedContact: 'Kontakt zaktualizowany',
      couldNotUpdateContact: 'Nie można zaktualizować kontaktu: {{error}}',
      updateContact: 'Aktualizuj kontakt',
      delete: 'Usuń',
      deleteContact: 'Usuń kontakt',
      deleteContactConfirmation: 'Czy na pewno chcesz usunąć kontakt {{name}}?',
      deleteContactError: 'Nie można usunąć kontaktu: {{error}}',
      deleteContactSuccess: 'Kontakt usunięty',
    },
    users: {
      users: 'Użytkownicy',
      inviteUser: 'Zaproś użytkownika',
      invitedUsers: 'Zaproszeni użytkownicy',
      noInvitedUsers: 'Brak zaproszonych użytkowników',
      removeInvite: 'Usuń zaproszenie',
      confirmRemoveInvite: 'Czy na pewno chcesz usunąć zaproszenie dla {{email}}?',
      inviteRemoved: 'Zaproszenie usunięte',
      couldNotRemoveInvite: 'Nie można usunąć zaproszenia: {{error}}',
      noUsersFound: 'Nie znaleziono użytkowników',
      removeUser: 'Usuń użytkownika',
      confirmRemoveUser: 'Czy na pewno chcesz usunąć użytkownika {{name}}?',
      userRemoved: 'Użytkownik usunięty',
      couldNotRemoveUser: 'Nie można usunąć użytkownika: {{error}}',
      active: 'Aktywny',
      inactive: 'Nieaktywny',
      userInvited: 'Użytkownik zaproszony',
      couldNotInviteUser: 'Nie można zaprosić użytkownika: {{error}}',
      name: 'Imię i nazwisko',
      email: 'Email',
      isActive: 'Jest aktywny?',
      invited: 'Zaproszony',
    },
    cmrReminder: {
      cmrReminder: 'Przypomnienie o CMR',
      sendReminder: 'Wyślij',
      confirmText: 'Czy na pewno chcesz wysłać przypomnienie o CMR?',
      confirmation: 'Przypomnienie o CMR wysłane',
      error: 'Nie można wysłać przypomnienia o CMR: {{error}}',
    },
    sustainability: {
      sustainability: 'Zrównoważony rozwój',
      policy: 'Polityka zrównoważonego rozwoju',
      viewOverview: 'Zobacz przegląd zrównoważonego rozwoju',
      orderCount: 'Liczba zamówień',
      totalDistance: 'Całkowity dystans',
      totalWeight: 'Całkowita waga',
      totalEmissions: 'Całkowite emisje CO2',
      averageDistance: 'Średni dystans',
      averageWeight: 'Średnia waga',
      averageEmissions: 'Średnie emisje CO2',
      total: 'Razem',
      avgPerOrder: 'Średnio na zamówienie',
    },
    settings: {
      settings: 'Ustawienia',
    },
    apiKeys: {
      apiKeys: 'Klucze API',
      createApiKey: 'Utwórz klucz API',
      name: 'Nazwa',
      scopes: 'Uprawnienia',
      status: 'Status',
      lastUsed: 'Ostatnio użyty',
      createdAt: 'Utworzono',
      prefix: 'Prefiks',
      active: 'Aktywny',
      revoked: 'Unieważniony',
      expired: 'Wygasły',
      never: 'Nigdy',
      revokeApiKey: 'Unieważnij klucz API',
      confirmRevoke: 'Czy na pewno chcesz unieważnić klucz API "{{name}}"? Tej operacji nie można cofnąć.',
      apiKeyRevoked: 'Klucz API został unieważniony',
      couldNotRevoke: 'Nie udało się unieważnić klucza API: {{error}}',
      apiKeyCreated: 'Klucz API został utworzony',
      couldNotCreate: 'Nie udało się utworzyć klucza API: {{error}}',
      copyKey: 'Kopiuj klucz',
      keyCopied: 'Klucz skopiowany',
      keyWarning: 'Przechowuj ten klucz w bezpiecznym miejscu. Nie zostanie ponownie wyświetlony.',
      noApiKeys: 'Nie znaleziono kluczy API',
      ordersRead: 'Odczyt zamówień',
      ordersWrite: 'Tworzenie zamówień',
      maxKeysReached: 'Osiągnięto maksymalną liczbę kluczy API',
      scopeRequired: 'Wymagany jest co najmniej jeden zakres',
    },
    loading: 'Ładowanie...',
  },
  liveTracking: {
    title: 'Śledzenie',
    subtitle: 'Położenie i komputer pokładowy tej ciężarówki.',
    loading: 'Pobieranie położenia…',
    noPosition: 'Położenie tej ciężarówki nie jest jeszcze znane.',
    noPositionHint: 'Powiąż pojazd z tą tablicą rejestracyjną na stronie integracji.',
    noConnection: 'Żadne połączenie śledzące nie obserwuje jeszcze tej ciężarówki. Połącz je, aby zobaczyć położenie.',
    driving: 'Jedzie',
    idle: 'Stoi',
    location: 'Położenie',
    speed: 'Prędkość',
    heading: 'Kierunek',
    odometer: 'Przebieg',
    fuel: 'Paliwo',
    lastMeasurement: 'Ostatni pomiar',
    liveActive: 'Śledzenie na żywo włączone — odświeżanie co 30 sekund.',
    lastSignal: 'Ostatni sygnał',
    trailer: 'Naczepa',
    refresh: 'Odśwież',
    startLive: 'Włącz śledzenie na żywo',
    stopLive: 'Wyłącz śledzenie na żywo',
    resizePanel: 'Zmień szerokość panelu bocznego',
    connections: 'Połączenia śledzące',
    connectionsSubtitle: 'Połącz środowisko śledzenia dostawcy, aby śledzić ich ciężarówki.',
    addConnection: 'Dodaj połączenie śledzenia',
    integrations: 'Integracje śledzenia',
    integrationsHint: 'Wybierz dostawcę, aby połączyć śledzenie przewoźnika.',
    providerFleetmaster: 'Śledź położenie i komputer pokładowy ciężarówek przez FleetMaster.lt.',
    connectionLabel: 'Nazwa',
    connectionLabelHelp: 'Jak to połączenie pojawi się na liście.',
    helpCompanyId: 'Identyfikator firmy z Twojego konta telematyki FleetMaster.',
    helpSuivoBaseUrl: 'Pozostaw puste dla standardowego środowiska Suivo.',
    ownerLabel: 'ID dostawcy',
    ownerHelp: 'Dla połączenia należącego do przewoźnika/dostawcy.',
    credentialsNote: 'Klucz jest przechowywany zaszyfrowany i po zapisaniu nie jest już nigdy pokazywany.',
    connect: 'Połącz',
    back: 'Wstecz',
    noConnections: 'Brak połączenia śledzącego.',
    noConnectionsHint: 'Dodaj je, aby zobaczyć, gdzie są ciężarówki.',
    moreConnectionsTitle: 'Więcej połączeń śledzenia już wkrótce',
    moreConnectionsBody:
      'FleetMaster jest pierwszym dostawcą. Pracujemy nad kolejnymi połączeniami śledzenia — nowi dostawcy pojawią się tutaj automatycznie.',
    connectionsLoading: 'Ładowanie połączeń…',
    vehiclesLoading: 'Ładowanie pojazdów…',
    noVehicles: 'To połączenie nie zgłasza jeszcze żadnych pojazdów.',
    statusActive: 'Aktywne',
    statusError: 'Błąd',
    keyLabel: 'klucz',
    linkedCount: '{{linked}} z {{total}} pojazdów powiązanych',
    lastChecked: 'ostatnia kontrola',
    never: 'nigdy',
    supplierOwner: 'Dostawca #{{id}}',
    connectionSettings: 'Ustawienia połączenia',
    verify: 'Sprawdź',
    verifyOk: 'Połączenie działa.',
    verifyFailed: 'Nie udało się sprawdzić połączenia.',
    remove: 'Usuń',
    removeTitle: 'Usuń połączenie',
    removeDescription: 'Połączenie „{{label}}” i zapisany klucz zostaną usunięte.',
    connectionRemoved: 'Połączenie usunięte.',
    linkVehicles: 'Połącz pojazdy',
    linkVehiclesHint: 'Wybierz pojazd, a następnie ciężarówkę, w której jeździ.',
    link: 'Powiąż',
    unlink: 'Odłącz',
    linkTruck: 'Powiąż ciężarówkę',
    chooseTruck: 'Ciężarówka',
    chooseTruckHelp: 'Wybierz ciężarówkę, w której jeździ ten pojazd.',
    chooseTruckRequired: 'Wybierz ciężarówkę',
    moreTrucks: 'Wyświetlane są tylko pierwsze {{count}} ciężarówki — wyszukaj, aby zawęzić listę.',
    plateMatch: 'tablica rejestracyjna się zgadza',
    linkedTo: 'Powiązano z {{truck}}',
    linkedManually: 'ręcznie',
    notLinked: 'Niepowiązane',
    vehicleLinked: 'Pojazd powiązany.',
    vehicleUnlinked: 'Powiązanie usunięte.',
    trailerSubtitle: 'Lokalizacja tej naczepy.',
    linkTracker: 'Połącz lokalizator',
    linkTrackerHint: 'Wybierz pojazd, który znajduje się w tej naczepie.',
    linkTrailer: 'Połącz naczepę',
    linkTrailerHint: 'Wybierz pojazd, a następnie naczepę, na której się znajduje.',
    chooseTrailer: 'Naczepa',
    chooseTrailerRequired: 'Wybierz naczepę',
    noTrackerLinked: 'Z tą naczepą nie jest jeszcze połączony żaden lokalizator — połącz go, aby zobaczyć lokalizację.',
    noTrailerPosition: 'Lokalizacja tej naczepy nie jest jeszcze znana.',
    searchUnits: 'Szukaj po nazwie lub rejestracji…',
    noUnits: 'Nie zgłoszono jeszcze żadnych pojazdów.',
    providerSuivo: 'Śledź położenie naszych naczep za pomocą trackerów Suivo.',
    ownerKindLabel: 'Właściciel',
    ownerKindCompany: 'Własna flota (firma)',
    ownerKindCarrier: 'Przewoźnik/dostawca',
    supplierOwnerCompany: 'Własna flota',
    status: {
      moving: 'W ruchu',
      parked: 'Zaparkowany',
      idling: 'Na biegu jałowym',
      immobilized: 'Zablokowany',
      offline: 'Brak sygnału',
      inOperation: 'W eksploatacji',
    },
    trailersMap: {
      searchLabel: 'Szukaj (ID TDR / nazwa / numer)',
      searchPlaceholder: 'Szukaj po nazwie lub numerze...',
      trackerLabel: 'Tracker',
      trackerAll: 'Wszystkie naczepy',
      trackerTracked: 'Z trackerem',
      trackerUntracked: 'Bez trackera',
      trackerSilent: 'Brak sygnału > 24 godz.',
      tableCardTitle: 'Naczepy',
      mapCardTitle: 'Mapa',
      tableTrailer: 'Naczepa',
      tablePlate: 'Numer',
      tableTracker: 'Tracker',
      tableStatus: 'Status',
      tableLastSignal: 'Ostatni sygnał',
      tablePlace: 'Miejsce',
      noTracker: 'Brak trackera',
      clearFilters: 'Wyczyść filtry',
      noneTrackedTitle: 'Tracker nie jest jeszcze podłączony',
      noneTrackedDescription: 'Podłącz urządzenie Suivo do naczepy, aby zobaczyć ją na mapie.',
      noneInFilter: 'Brak naczep w tym wyborze',
      noConnectionsTitle: 'Brak połączenia śledzenia',
      noConnectionsDescription: 'Dodaj połączenie, aby zobaczyć, gdzie są nasze naczepy.',
      noConnectionsAction: 'Do integracji śledzenia',
      connectionErrorTitle: 'Połączenie śledzenia z błędem',
    },
  },
  carrierPortal: {
    liveTracking: { liveTracking: 'Śledzenie' },
    selectCarrier: 'Wybierz przewoźnika',
    loading: 'Ładowanie...',
    switch: 'Zmień przewoźnika',
    home: {
      carrierPortal: 'Portal Przewoźnika',
      title: 'Portal Przewoźnika',
      hello: 'Witaj,',
      welcome: 'Witaj w portalu przewoźnika.',
      manageOrders: 'Zarządzaj wszystkimi swoimi zleceniami transportowymi.',
      managePlanning: 'Zarządzaj swoim planowaniem.',
      selfBilling: 'Twórz i zarządzaj swoimi fakturami samofakturującymi.',
      info: 'Informacje',
      infoSubtitle: 'Informacje o TransDirect i portalu przewoźnika',
      contact: 'Osoba kontaktowa',
      contactSubtitle: 'Kto jest Twoją osobą kontaktową w TransDirect',
      FuelCard: 'Karta paliwowa',
      FuelCardSubtitle: 'Zamów spersonalizowaną kartę paliwową TransDirect',
      saveChanges: 'Zapisz zmiany',
    },
    orders: {
      allAccepted: 'Wszystkie zamówienia potwierdzone',
      customerReference: 'Referencja klienta',
      draft: 'Szkic',
      load: 'Załadunek',
      orders: 'Zamówienia',
      manageOrders: 'Zarządzaj wszystkimi swoimi transportami i przesyłaj dokumenty transportowe',
      orderRef: 'Referencja TD',
      loadLocation: 'Miejsce załadunku',
      unload: 'Rozładunek',
      unloadLocation: 'Miejsce rozładunku',
      invoiceTotal: 'Stawka frachtowa',
      hasCMR: 'Posiada dokumenty',
      documents: 'Dokumenty',
      cmrRejected: 'Odrzucono',
      cmrPending: 'W trakcie',
      cmrUploadHint: 'Możesz przesłać jeden lub więcej plików PDF; połączymy je w jeden dokument.',
      cmrUploaded: 'Dokument przesłany',
      cmrUploadFailed: 'Nie udało się przesłać dokumentu: {{error}}',
      documentValidation: 'Dokumenty',
      documentValidationNoDocuments: 'Brak dokumentów',
      documentValidationPending: 'W trakcie',
      documentValidationApproved: 'Zatwierdzono',
      documentValidationRejected: 'Odrzucono',
      outstandingDocsTitle: 'Documents still required',
      requiredDocMissing: 'missing',
      requiredDocInReview: 'under review',
      requiredDocRejected: 'rejected — please re-upload',
      requiredDocStatusReceived: 'Received',
      requiredDocStatusInReview: 'In review',
      requiredDocStatusRejected: 'Rejected',
      requiredDocStatusMissing: 'Missing',
      requiredDocsTitle: 'Required documents',
      requiredDocsSubtitle: 'This customer requires the documents below for this order.',
      requiredDocsProgress: '{{done}} of {{total}} received',
      requiredDocReupload: 'Upload again',
      uploadDocumentTitle: 'Upload {{document}}',
      uploadRequiredDoc: 'Upload',
      uploadDocumentsTitle: 'Prześlij dokumenty',
      uploadDocumentsHint:
        'Prześlij jeden lub więcej plików naraz; podzielimy je i przypiszemy każdą część do właściwego typu dokumentu.',
      docReasonInReview: 'w trakcie weryfikacji',
      docReasonRejected: 'odrzucony',
      docRejectionLowQuality: 'nieczytelny',
      docRejectionMissingSignature: 'brak podpisu',
      docRejectionNoOrderReference: 'nie znaleziono referencji',
      docRejectionNotNeutral: 'CMR nie jest neutralny',
      docRejectionWrongCustomer: 'niewłaściwy klient',
      docRejectionWrongDeliveryAddress: 'niewłaściwy adres dostawy',
      docRejectionWrongDocumentType: 'niewłaściwy typ dokumentu',
      docRejectionWrongOrgOnDocument: 'niewłaściwa firma',
      requiredDocUploaded: 'Document uploaded',
      requiredDocUploadFailed: 'Document could not be uploaded: {{error}}',
      purchases: 'Stawka frachtowa',
      reference: 'Referencja',
      assignee: 'Przypisano do',
      orderAssigned: 'Zamówienie przypisane',
      changeAssignee: 'Zmień',
      assignOrder: 'Przypisz zamówienie',
      assign: 'Przypisz',
      route: 'Trasa',
      error: 'Błąd',
      report: 'Raport',
    },
    planning: {
      acceptAll: 'Zaakceptuj wszystkie',
      actions: 'Akcje',
      date: 'Data',
      loadingOrders: 'Ładowanie zamówień...',
      noTruck: 'Ten przewoźnik nie ma jeszcze ciężarówki.',
      ordersForTruck: 'Zamówienia dla ciężarówki o numerze rejestracyjnym {{licensePlate}}',
      planning: 'Planowanie',
      truck: 'Ciężarówka',
      managePlanning: 'Przeglądaj i zarządzaj swoim planowaniem transportu',
    },
    selfBilling: {
      selfBilling: 'Samofakturowanie',
      rideOverview: 'Zestawienie przejazdów',
      manageRideOverview: 'Przeglądaj i pobieraj swoje zestawienia przejazdów',
      searchRideOverviewPlaceholder: 'Wyszukaj zestawienie przejazdów...',
      type: 'Typ',
      number: 'Numer',
      manageSelfBilling: 'Przeglądaj i pobieraj swoje samofakturowanie',
      searchPlaceholder: 'Wyszukaj samofakturowanie...',
      id: 'Numer referencyjny',
      date: 'Data',
      download: 'Pobierz',
      invoice: 'Faktura',
      title: 'Faktura samofakturująca',
    },
    businessInformation: {
      businessInformation: 'Informacje o firmie',
      settingsAndBusinessInformation: 'Ustawienia i informacje o firmie',
      tabGeneral: 'Ogólne',
      tabFleet: 'Flota',
      tabContacts: 'Kontakty',
      civilLiabilityInsurance: 'Ubezpieczenie OC',
      CMRInsurance: 'Ubezpieczenie CMR',
      transportationInsurance: 'Ubezpieczenie transportowe',
      documentUploaded: 'Dokument przesłany',
      insurances: 'Ubezpieczenia',
    },
    generalInformation: {
      generalInformation: 'Informacje',
      noDocumentsAvailable: 'Brak dostępnych dokumentów',
      checkBackLater: 'Sprawdź później zaktualizowaną dokumentację',
    },

    contacts: {
      contacts: 'Kontakty',
      newContact: 'Nowy kontakt',
      updateContact: 'Zapisz kontakt',
      name: 'Imię i nazwisko',
      email: 'Email',
      phone: 'Telefon',
      function: 'Funkcja',
      language: 'Język',
      delete: 'Usuń',
      createContactSuccess: 'Kontakt utworzony pomyślnie',
      createContactError: 'Błąd podczas tworzenia kontaktu: {{error}}',
      updateContactSuccess: 'Kontakt zaktualizowany pomyślnie',
      updateContactError: 'Błąd podczas aktualizacji kontaktu: {{error}}',
      deleteContact: 'Usuń kontakt',
      deleteContactConfirmation: 'Czy na pewno chcesz usunąć swój kontakt?',
      deleteContactSuccess: 'Kontakt usunięty pomyślnie',
      deleteContactError: 'Błąd podczas usuwania kontaktu: {{error}}',
      notFound: 'Kontakt nie znaleziony',
      notifications: 'Powiadomienia',
    },
    trucks: {
      title: 'Ciężarówki',
      newTruck: 'Nowa ciężarówka',
      update: 'Aktualizuj ciężarówkę',
      licensePlate: 'Numer rejestracyjny',
      codeName: 'Nazwa kodowa',
      planner: 'Planista',
      isActive: 'Aktywny',
      insuranceExpiresAt: 'Ubezpieczenie wygasa',
      examinationExpiresAt: 'Badanie techniczne wygasa',
      documents: 'Dokumenty',
      yes: 'Tak',
      no: 'Nie',
      moveUp: 'Przesuń w górę',
      moveDown: 'Przesuń w dół',
      create: 'Utwórz',
      succeeded: 'Ciężarówka zapisana',
      createError: 'Nie można utworzyć ciężarówki: ',
      updateError: 'Nie można zaktualizować ciężarówki: ',
      insurance: 'Ubezpieczenie',
      insuranceUploaded: 'Ubezpieczenie zapisane',
      examination: 'Badanie techniczne',
      examinationUploaded: 'Badanie techniczne zapisane',
      notFound: 'Nie znaleziono ciężarówek',
      appLoginTitle: 'Logowanie do aplikacji kierowcy',
      appLoginHint:
        'Kierowcy logują się numerem rejestracyjnym. Ustaw hasło tutaj; kierowcy nie mogą go sami zresetować.',
      appPasswordLabel: 'Hasło aplikacji kierowcy',
      appLoginLabel: 'Login (numer rejestracyjny)',
      appCodeNameLabel: 'Nazwa kodowa',
      appSavePassword: 'Zapisz hasło',
      appDisableLogin: 'Wyłącz logowanie',
      appPasswordSaved: 'Hasło zapisane',
      appLoginDisabled: 'Logowanie do aplikacji kierowcy wyłączone',
      detailsTitle: 'Dane ciągnika',
      documentCount: '{{count}} z {{total}}',
      statusValid: 'Ważny',
      statusExpiringSoon: 'Wkrótce wygasa',
      statusExpired: 'Wygasł',
      statusMissing: 'Brak',
      expiresOn: 'Wygasa {{date}}',
      noDocument: 'Nie przesłano jeszcze dokumentu',
      viewDocument: 'Podgląd',
      replaceDocument: 'Zamień',
      uploadDocument: 'Prześlij',
      searchPlaceholder: 'Szukaj po numerze rejestracyjnym…',
      noExpiryKnown: 'Brak znanej daty ważności',
      notUploadedYet: 'Jeszcze nie przesłano',
    },
    trailers: {
      title: 'Naczepy',
      truckAmount: 'Liczba ciężarówek',
      tautlinerAmount: 'Liczba firanek',
      boxTrailerAmount: 'Liczba naczep typu chłodnia/izoterma',
      openTrailerAmount: 'Liczba naczep otwartych',
      save: 'Zapisz zmiany',
      saveSuccess: 'Przewoźnik zaktualizowany',
      saveError: 'Nie można zaktualizować przewoźnika: {{error}}',
      subtitle: 'Liczba według typu — używana do planowania przepustowości',
    },
    driverBlock: {
      title: 'Dostęp kierowcy',
      message: 'Zaloguj się do aplikacji kierowcy na swoim telefonie, aby zobaczyć swoje zlecenia.',
    },
    notFound: {
      pageNotFound: 'Strona nie znaleziona.',
      pageNotFoundDesc:
        'Ta strona wydaje się nie istnieć (jeszcze). Jeśli uważasz, że coś jest nie tak, skontaktuj się z osobą kontaktową w TransDirect.',
    },
    invite: {
      title: 'Zaproszenie do Portalu Przewoźnika',
      details: 'Zostałeś zaproszony do portalu przewoźnika TransDirect, utwórz swoje konto, wprowadzając hasło.',
      accepted: 'Zaproszenie przyjęte, wkrótce zostaniesz przekierowany na stronę główną.',
      acceptError: 'Nie można przyjąć zaproszenia: {{error}}',
      acceptButtonText: 'Przyjmij zaproszenie',
      password: 'Hasło',
      repeatPassword: 'Powtórz hasło',
    },
    users: {
      users: 'Użytkownicy',
      inviteUser: 'Zaproś użytkownika',
      invitedUsers: 'Zaproszeni użytkownicy',
      noInvitedUsers: 'Brak zaproszonych użytkowników',
      removeInvite: 'Usuń zaproszenie',
      confirmRemoveInvite: 'Czy na pewno chcesz usunąć zaproszenie dla {{email}}?',
      inviteRemoved: 'Zaproszenie usunięte',
      couldNotRemoveInvite: 'Nie można usunąć zaproszenia: {{error}}',
      noUsersFound: 'Nie znaleziono użytkowników',
      removeUser: 'Usuń użytkownika',
      confirmRemoveUser: 'Czy na pewno chcesz usunąć użytkownika {{name}}?',
      userRemoved: 'Użytkownik usunięty',
      couldNotRemoveUser: 'Nie można usunąć użytkownika: {{error}}',
      active: 'Aktywny',
      inactive: 'Nieaktywny',
      userInvited: 'Użytkownik zaproszony',
      couldNotInviteUser: 'Nie można zaprosić użytkownika: {{error}}',
      name: 'Imię i nazwisko',
      email: 'Email',
      isActive: 'Jest aktywny?',
      role: 'Rola',
      roleDispatcher: 'Dyspozytor',
      roleDriver: 'Kierowca',
      activeUsers: 'Aktywni użytkownicy',
      pendingInvitations: 'Oczekujące zaproszenia',
      sendInvitation: 'Wyślij zaproszenie',
      sending: 'Wysyłanie...',
      cancel: 'Anuluj',
      emailAddress: 'Adres email',
      deleteInvitation: 'Usuń zaproszenie',
      joined: 'Dołączył',
      invited: 'Zaproszony',
      assignee: 'Przypisano do',
      assignOrder: 'Przypisz zamówienie',
      unassigned: 'Nieprzypisane',
      assignedTo: 'Przypisano do',
      roleUpdated: 'Rola zaktualizowana',
      renameUser: 'Zmień nazwę użytkownika',
      newName: 'Nowa nazwa',
      userRenamed: 'Nazwa użytkownika zmieniona',
      save: 'Zapisz',
      permissions: 'Uprawnienia',
      canRenameUsers: 'Może zmieniać nazwy użytkowników',
      permissionsUpdated: 'Uprawnienia zaktualizowane',
      nameOptional: 'Nazwa (opcjonalnie)',
      emailRequired: 'Wprowadź adres e-mail',
      namePlaceholder: 'Jan Kowalski',
      emailPlaceholder: 'uzytkownik@przyklad.com',
    },
    tracking: {
      loading: 'Ładowanie wydarzeń śledzenia...',
      events: 'Wydarzenia śledzenia',
      noEvents: 'Nie znaleziono wydarzeń śledzenia',
      scheduled: 'Zaplanowane',
      eventTypes: {
        StatusLoaded: 'Załadowano',
        StatusUnloaded: 'Rozładowano',
        LocationPing: 'Aktualizacja lokalizacji',
      },
      stop: 'Przystanek',
      by: 'przez',
    },
  },
  carrierContactNotifications: {
    transportOrders: 'Zlecenia transportowe',
    selfBillings: 'Samofakturowanie',
  },
  orderStatus: {
    Draft: 'Szkic',
    Created: 'Utworzono',
    Prepayment: 'Przedpłata',
    Placed: 'Złożono',
    Confirmed: 'Potwierdzono',
    Refused: 'Odrzucono',
    Executed: 'Wykonano',
    Cancelled: 'Anulowano',
  },
  orderSaleStatus: {
    Created: 'Utworzono',
    Credited: 'Zaksięgowano',
    Incomplete: 'Niekompletne',
    Invoiced: 'Zafakturowano',
  },
  customerContactNotifications: {
    invoice: 'Faktura',
    orderConfirmation: 'Potwierdzenie zamówienia',
    cmr: 'CMR',
    quotation: 'Wycena',
    dieselSurcharge: 'Dopłata paliwowa',
    paymentReminder: 'Przypomnienie o płatności',
  },
  errors: {
    required: 'Wymagane',
    invalidEmail: 'Nieprawidłowy email',
    dateShouldBeInTheFuture: 'Data powinna być w przyszłości',
    startTimeShouldBeBeforeEndTime: 'Czas rozpoczęcia powinien być przed czasem zakończenia',
    passwordsDontMatch: 'Hasło i powtórzone hasło nie pasują do siebie.',
    passwordTooShort: 'Hasło jest za krótkie.',
    formValidation: 'Sprawdź zaznaczone pola w formularzu',
    retry: 'Spróbuj ponownie',
    reloadPage: 'Odśwież stronę',
    networkTitle: 'Problem z połączeniem',
    networkDescription: 'Nie możemy połączyć się z serwerem. Sprawdź połączenie internetowe i spróbuj ponownie.',
    serverTitle: 'Błąd serwera',
    serverDescription: 'Serwer nie mógł przetworzyć żądania. Spróbuj ponownie później.',
    notFoundTitle: 'Nie znaleziono',
    notFoundDescription: 'Żądane dane już nie istnieją lub są niedostępne.',
    sessionTitle: 'Sesja wygasła',
    sessionDescription: 'Sesja wygasła lub nie jesteś zalogowany. Zaloguj się ponownie, aby kontynuować.',
    permissionTitle: 'Brak dostępu',
    permissionDescription: 'Nie masz dostępu do tych danych.',
    validationTitle: 'Nieprawidłowe dane',
    validationDescription: 'Sprawdź wprowadzone dane i spróbuj ponownie.',
    rateLimitTitle: 'Zbyt wiele żądań',
    rateLimitDescription: 'Wysłano zbyt wiele żądań. Poczekaj chwilę i spróbuj ponownie.',
    abortedTitle: 'Żądanie anulowane',
    abortedDescription: 'Żądanie zostało anulowane. W razie potrzeby spróbuj ponownie.',
    unknownTitle: 'Nieznany błąd',
    unknownDescription:
      'Wystąpił nieoczekiwany błąd. Spróbuj ponownie lub skontaktuj się z pomocą, jeśli problem będzie się powtarzał.',
    loadingTitle: 'Ładowanie nie powiodło się',
    loadingDescription: 'Nie udało się załadować danych.',
  },
  orderComplaintType: {
    Aggression: 'Agresja fizyczna/słowna',
    DamagedGoods: 'Uszkodzony towar',
    IncorrectDelivery: 'Nieprawidłowa dostawa',
    IncorrectInvoice: 'Nieprawidłowa faktura',
    LateDelivery: 'Opóźniona dostawa',
    Other: 'Inne',
  },
  myProfile: {
    myProfile: 'Mój profil',
    logout: 'Wyloguj',
    name: 'Imię i nazwisko',
    role: 'Rola',
    email: 'Email',
    language: 'Język',
    languageChanged: 'Język zaktualizowany',
    languageNotChanged: 'Nie można zaktualizować języka: {{error}}',
    joinedAt: 'Dołączył',
    nameChanged: 'Imię i nazwisko zostało zaktualizowane',
    nameNotChanged: 'Nie można zaktualizować imienia i nazwiska: {{error}}',
  },
  auth: {
    forgotPassword: {
      title: 'Zapomniałeś hasła',
      requestNewPassword: 'Zażądaj nowego hasła',
      instructions:
        'Wprowadź swój email, jeśli użytkownik istnieje w systemie, wkrótce otrzymasz email, aby ustawić nowe hasło.',
      requestFailed: 'Nie można zażądać nowego hasła',
      instructionsSent: 'Instrukcje dotyczące ustawienia nowego hasła zostały wysłane na Twój adres email.',
    },
    configNewPassword: {
      title: 'Ustaw nowe hasło',
      newPassword: 'Nowe hasło',
      repeatNewPassword: 'Powtórz nowe hasło',
      confirmButtonText: 'Ustaw nowe hasło',
      passwordDontMatch: 'Hasło i powtórzone hasło muszą być takie same',
      tokenNotFound: 'Token nie znaleziony',
      invalidToken: 'Token jest już nieważny',
      configFailed: 'Nie można ustawić hasła: {{error}}',
    },
    login: {
      login: 'Zaloguj',
      email: 'Email',
      password: 'Hasło',
      forgotPassword: 'Zapomniałeś hasła',
      customerLoginTitle: 'Logowanie klienta',
      loginWithGoogle: 'Zaloguj się przez Google',
      incorrectCredentials: 'Email lub hasło są nieprawidłowe',
      loginFailed: 'Nie można się zalogować: {{error}}',
    },
    userNotFound: {
      title: 'Użytkownik nie znaleziony',
      description: 'Nie można się zalogować, wystąpił błąd lub użytkownik już nie istnieje.',
      goToLoginPage: 'Przejdź do strony logowania',
    },
  },
  upload: {
    title: 'Prześlij',
    dropzoneHint: 'Kliknij tutaj lub przeciągnij plik(i) do pola, aby przesłać',
    uploadDocuments: 'Prześlij dokumenty',
    confirmDeleteDocument: 'Czy na pewno chcesz usunąć ten dokument?',
    deleteDocument: 'Usuń dokument',
    noDocumentsFound: 'Nie znaleziono dokumentów',
    noDocumentsSelected: 'Nie wybrano żadnych dokumentów',
    uploadFailed: 'Nie można przesłać dokumentu',
    uploadDocumentFailed: 'Nie można przesłać pliku {{filename}}: {{reason}}',
    multiUploadFailed: 'Nie można przesłać plików: {{reason}}',
  },
  or: 'lub',
  draft: 'Wersja robocza',
  cancel: 'Anuluj',
  close: 'Zamknij',
  moreInfo: 'Więcej informacji',
  previous: 'Poprzedni',
  next: 'Następny',
  delete: 'Usuń',
  save: 'Išsaugoti',
  download: {
    open: 'Otwórz',
    download: 'Pobierz',
    started: 'Pobieranie rozpoczęte',
    failed: 'Pobieranie nie powiodło się: {{message}}',
  },
  chat: {
    title: 'Czat',
    sendMessage: 'Wyślij wiadomość',
    placeholder: 'Wpisz wiadomość...',
    noMessages: 'Brak wiadomości',
    loadMore: 'Załaduj więcej wiadomości',
    messageSent: 'Wiadomość wysłana',
    messageError: 'Nie udało się wysłać wiadomości: {{error}}',
    newMessage: '{{name}}: nowa wiadomość w zamówieniu {{orderNumber}}',
    internal: 'Wewnętrzny',
    carrier: 'Przewoźnik',
  },
};
